// Client Portal — Prior Authorization Support (PAS) portal. Extracted from App.js.
import { useState, useEffect, useRef } from "react";
import {
  BarChart3,
  Bell,
  ClipboardCheck,
  FileText,
  Key,
  Loader2,
  Palette,
  Send,
  ShieldCheck,
  Sliders,
  Users,
} from "lucide-react";
import { authFetch } from "../lib/api";
import { KBPanel, BriefingsPanel, KB_ROLE_LEVELS } from "./RAGChat";
import { RAGApiKeys } from "./RAGApiKeys";
import { RAGAuditLog } from "./RAGAuditLog";
import { RAGCustomize } from "./RAGCustomize";
import { RAGPromptStudio } from "./RAGPromptStudio";
import { UserManagement } from "../components/UserManagement";
import { SystemBanner } from "../components/SystemBanner";
import { UsageAnalyticsDashboard } from "../analytics/UsageAnalyticsDashboard";

const PACriteriaLookup = ({ facility, user }) => {
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const fid = facility.facility_id;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const systemContext =
    "You are a prior authorization support assistant. The user will describe a clinical case, procedure, drug, or payer. Help them understand coverage criteria, step-therapy requirements, and what documentation to include in a PA request. Always cite sources where available.";

  const submit = async () => {
    const text = query.trim();
    if (!text || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg]);
    setQuery("");
    setLoading(true);
    try {
      const res = await authFetch(
        `/rag/chat?facility_id=${encodeURIComponent(fid)}`,
        {
          method: "POST",
          body: JSON.stringify({
            message: text,
            system_context: systemContext,
          }),
        },
      );
      const reply = res?.reply || res?.answer || "No response received.";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: "Failed to get a response. Please try again.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-[600px]">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-teal-600" />
        <div>
          <p className="text-sm font-semibold text-slate-800">
            PA Criteria Lookup
          </p>
          <p className="text-xs text-slate-500">
            Describe the case — payer, procedure, drug, or diagnosis — to get
            coverage criteria and PA guidance.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
            <ClipboardCheck className="w-10 h-10 opacity-30" />
            <p className="text-sm text-center max-w-xs">
              Ask about coverage criteria, step therapy, formulary status, or
              what to include in a PA request.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                "What are Aetna's criteria for lumbar fusion (CPT 22612)?",
                "Is Dupixent covered for atopic dermatitis under BlueCross?",
                "What step therapy is required for Humira?",
              ].map((s) => (
                <button
                  key={s}
                  onClick={() => setQuery(s)}
                  className="text-xs px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full border border-teal-200 hover:bg-teal-100 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm ${
                m.role === "user"
                  ? "bg-teal-600 text-white"
                  : "bg-slate-100 text-slate-800"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-xl px-4 py-2.5">
              <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="px-4 py-3 border-t border-slate-100 flex gap-2">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Describe the case, procedure, drug, or payer…"
          rows={2}
          className="flex-1 resize-none px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        />
        <button
          onClick={submit}
          disabled={!query.trim() || loading}
          className="self-end px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const PASPortal = ({
  facility,
  user,
  systemMessages = [],
  onDismissMessage,
}) => {
  const [tab, setTab] = useState("pa-lookup");
  const kbRole =
    facility.kb_role ||
    facility.services?.find((s) => s.service_type === "pas")?.access_role ||
    "viewer";
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const isAdmin = kbRoleGte("admin");

  const tabs = [
    { id: "pa-lookup", label: "PA Lookup", icon: ClipboardCheck },
    { id: "knowledge-base", label: "Knowledge Base", icon: FileText },
    { id: "briefings", label: "Briefings", icon: Bell },
    ...(isAdmin
      ? [
          { id: "users", label: "Users", icon: Users },
          { id: "analytics", label: "Analytics", icon: BarChart3 },
          { id: "api-keys", label: "API Keys", icon: Key },
          { id: "prompt-studio", label: "Prompt Studio", icon: Sliders },
          { id: "audit-log", label: "Audit Log", icon: ShieldCheck },
          { id: "customize", label: "Customize", icon: Palette },
        ]
      : []),
  ];

  return (
    <div className="space-y-4">
      {systemMessages.map((msg) => (
        <SystemBanner key={msg.id} message={msg} onDismiss={onDismissMessage} />
      ))}
      <div className="flex space-x-1 bg-white rounded-lg shadow-sm border border-slate-200 p-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              tab === t.id
                ? "bg-teal-600 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>
      {tab === "pa-lookup" && (
        <PACriteriaLookup facility={facility} user={user} />
      )}
      {tab === "knowledge-base" && (
        <KBPanel facility={facility} user={user} kbRole={kbRole} />
      )}
      {tab === "briefings" && <BriefingsPanel facility={facility} />}
      {tab === "users" && isAdmin && <UserManagement facility={facility} />}
      {tab === "analytics" && isAdmin && (
        <UsageAnalyticsDashboard facility={facility} />
      )}
      {tab === "api-keys" && isAdmin && (
        <RAGApiKeys facility={facility} user={user} />
      )}
      {tab === "prompt-studio" && isAdmin && (
        <RAGPromptStudio facility={facility} user={user} />
      )}
      {tab === "audit-log" && isAdmin && <RAGAuditLog facility={facility} />}
      {tab === "customize" && isAdmin && <RAGCustomize facility={facility} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// PATIENT AFTERCARE PORTAL
// ═══════════════════════════════════════════════════════════════════
